/**
  Author: Fernand Gonzales
  Date Created: 2024-10-31

  Purpose: JS handler for all of the DrawerPro configurator logic.

  Related files:
    - templates/page.drawerpro-template.liquid
    - assets/drawerpro.css
    - assets/Drawerpro.js
*/

import ConfiguratorEventHandler from "./ConfiguratorEventHandler.js";
import ConfiguratorState from "./ConfiguratorState.js";
import DrawerProProduct from "./DrawerProProduct.js";
import { Tools } from "./DrawerProEnums.js";

/**
 * Represents and instance of a DrawerPro configurator.
 *
 * @property Glide {Glide} - Glide.JS instance
 * @property Selectors {object} - all main HTML element selectors within the configurator instance
 * @property NumSteps {int} - number of steps within the configurator instance
 * @property toolsSelected {bool} - tells us whether or not a valid selection has been made in the current step.
 *                                           Used to tell us whether or not to enable the "next step" button.
 * @property CartList {object[]} - array of objects that stores all of the different DrawerPro SKUs and their quantities. This is updated every time
 *                                          the user clicks on the "next" button. On the last cart review page, we do the actual processing against this array
 *                                          in order to render all of the product cards for the user to review before adding all of them to the cart.
 * @property StepMap {String[]} - array of strings that holds the filtered step names of the configurator
 * @property currentStep {object{stepIndex, stepName}} - object that holds the current step's index and name
 * @property colourSelections {object} - props: metric, sae, and other. Holds human-readable colour names after the user makes their
 * @property State {ConfiguratorState} - state object for the configurator.
 * @property EventHandlers {object} - holds all of the functions for event handlers that point to ConfiguratorEventHandler static methods.
 * @property URLParams {URLSearchParams}
 * @property QRCode {QRious} - instance of the QRious library for QR code generation.
 *
 * @property @private StepNameMap {object} - JSON mapping with stepNames as the keys and their associated stepTitles as their values.
 * @property @private InitialStepState {Element} - a copy of the base initial step track. It's use-case is for resetting the state of the
 *                                                 confiugrator when steps get filtered out.
 *                                                selection of prefered colours.
 * @property @private ToolHolderMap {object} - JSON mapping of all of the available tools that DrawerPro is compatible with. Ultimately tells us how many
 *                                             clips are required based on tool, tool properties (drive size, handle, etc), and the user's prefered colour.
 *                                             This is meant to store value of how many **individual clips** are required for each tool organized by DrawerPro.
 * @property @private ToolHolderTypeMap {object} - static JSON mapping of all unique enums for tool IDs (as keys) with their "type" of holder as the values.
 *
 *
 * @class
 */
export default class DrawerPro {
  Glide;
  Selectors;
  NumSteps;
  toolsSelected;
  CartList = {}; // Object that holds all of the DrawerProProducts by step ID
  StepMap;
  currentStep = {};
  colourSelections;
  State;
  EventHandlers;
  URLParams;
  QRCode;

  #StepNameMap;
  #InitialStepState;
  ToolHolderMap; // https://docs.google.com/spreadsheets/d/1CyhbjEoJ0MfrEiKC0dFPuiH_7t98o_9V/edit?gid=1803605712#gid=1803605712
  #ToolHolderTypeMap;

  constructor() {
    this.State = new ConfiguratorState([], null, null, null);
    this.Selectors = this.__refreshSelectors();
    this.#InitialStepState = this.Selectors.StepTrack.cloneNode(true);
    this.QRCode = new QRious({
      element: this.Selectors.QRCodeCanvas,
    });

    this.Glide = new Glide(".body__main", {
      type: "slider",
      rewind: false,
      gap: 0,
      peek: 0,
      keyboard: false,
      swipeThreshold: false,
      dragThreshold: false,
      animationDuration: 200,
    });

    this.URLParams = new URLSearchParams(window.location.search);

    this.StepMap = [];
    this.colourSelections = {
      metric: null,
      sae: null,
      other: null,
    };
    this.toolsSelected = false;

    this.#ToolHolderTypeMap = {
      [Tools.METRIC.WRENCH_SETS]: "metric",
      [Tools.SAE.WRENCH_SETS]: "sae",
      [Tools.METRIC.LARGE_WRENCHES]: "metric",
      [Tools.SAE.LARGE_WRENCHES]: "sae",
      [Tools.OTHER.BREAKER_BARS]: "other",
      [Tools.OTHER.SCREWDRIVERS]: "other",
      [Tools.OTHER.EXTENSIONS]: "other",
      [Tools.OTHER.SMALL_PICKS]: "other",
      [Tools.OTHER.LARGE_PICKS]: "other",
      [Tools.OTHER["14_DRIVE"].STEEL_RATCHETS]: "other",
      [Tools.OTHER["38_DRIVE"].STEEL_RATCHETS]: "other",
      [Tools.OTHER["12_DRIVE"].STEEL_RATCHETS]: "other",
      [Tools.OTHER["14_DRIVE"].ERGO_RATCHETS]: "other",
      [Tools.OTHER["38_DRIVE"].ERGO_RATCHETS]: "other",
      [Tools.OTHER["12_DRIVE"].ERGO_RATCHETS]: "other",
      [Tools.OTHER["14_DRIVE"].SBM_TQWR]: "other",
      [Tools.OTHER["38_DRIVE"].SBM_TQWR]: "other",
      [Tools.OTHER["12_DRIVE"].SBM_TQWR]: "other",
      [Tools.OTHER["14_DRIVE"].CLK_TQWR]: "other",
      [Tools.OTHER["38_DRIVE"].CLK_TQWR]: "other",
      [Tools.OTHER["12_DRIVE"].CLK_TQWR]: "other",
    };

    this.ToolHolderMap = {
      [Tools.OTHER["14_DRIVE"].STEEL_RATCHETS]: [
        new DrawerProProduct(
          "DPRO-RH-14-3PC",
          3,
          1,
          '1/4" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-S-10PC", 10, 1, "Small handle holder"),
      ],
      [Tools.OTHER["38_DRIVE"].STEEL_RATCHETS]: [
        new DrawerProProduct(
          "DPRO-RH-38-3PC",
          3,
          1,
          '3/8" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-S-10PC", 10, 1, "Small handle holder"),
      ],
      [Tools.OTHER["12_DRIVE"].STEEL_RATCHETS]: [
        new DrawerProProduct(
          "DPRO-RH-12-3PC",
          3,
          1,
          '1/2" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
      [Tools.OTHER["14_DRIVE"].ERGO_RATCHETS]: [
        new DrawerProProduct(
          "DPRO-RH-14-3PC",
          3,
          1,
          '1/4" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
      [Tools.OTHER["38_DRIVE"].ERGO_RATCHETS]: [
        new DrawerProProduct(
          "DPRO-RH-38-3PC",
          3,
          1,
          '3/8" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
      [Tools.OTHER["12_DRIVE"].ERGO_RATCHETS]: [
        new DrawerProProduct(
          "DPRO-RH-12-3PC",
          3,
          1,
          '1/2" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
      [Tools.OTHER.SCREWDRIVERS]: [
        new DrawerProProduct("DPRO-HH-S-10PC", 10, 1, "Small handle holder"),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
      [Tools.METRIC.WRENCH_SETS]: [
        new DrawerProProduct("DPRO-WH-S-5PC", 5, 5, "Small wrench holder"),
        new DrawerProProduct("DPRO-WH-M-5PC", 5, 5, "Medium wrench holder"),
        new DrawerProProduct("DPRO-WH-L-5PC", 5, 5, "Large wrench holder"),
      ],
      [Tools.SAE.WRENCH_SETS]: [
        new DrawerProProduct("DPRO-WH-S-5PC", 5, 5, "Small wrench holder"),
        new DrawerProProduct("DPRO-WH-M-5PC", 5, 5, "Medium wrench holder"),
        new DrawerProProduct("DPRO-WH-L-5PC", 5, 5, "Large wrench holder"),
      ],
      [Tools.METRIC.LARGE_WRENCHES]: [
        new DrawerProProduct("DPRO-WH-XL-1PC", 1, 1, "XL wrench holder"),
      ],
      [Tools.SAE.LARGE_WRENCHES]: [
        new DrawerProProduct("DPRO-WH-XL-1PC", 1, 1, "XL wrench holder"),
      ],
      [Tools.OTHER["14_DRIVE"].CLK_TQWR]: [
        new DrawerProProduct(
          "DPRO-RH-14-3PC",
          3,
          1,
          '1/4" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
      [Tools.OTHER["38_DRIVE"].CLK_TQWR]: [
        new DrawerProProduct(
          "DPRO-RH-38-3PC",
          3,
          1,
          '3/8" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
      [Tools.OTHER["12_DRIVE"].CLK_TQWR]: [
        new DrawerProProduct(
          "DPRO-RH-12-3PC",
          3,
          1,
          '1/2" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
      [Tools.OTHER["14_DRIVE"].SBM_TQWR]: [
        new DrawerProProduct(
          "DPRO-RH-14-3PC",
          3,
          1,
          '1/4" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-L-S-10PC", 10, 2, "Small L-holder"),
      ],
      [Tools.OTHER["38_DRIVE"].SBM_TQWR]: [
        new DrawerProProduct(
          "DPRO-RH-38-3PC",
          3,
          1,
          '3/8" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-L-S-10PC", 10, 2, "Small L-holder"),
      ],
      [Tools.OTHER["12_DRIVE"].SBM_TQWR]: [
        new DrawerProProduct(
          "DPRO-RH-12-3PC",
          3,
          1,
          '1/2" ratchet head holder',
        ),
        new DrawerProProduct("DPRO-L-S-10PC", 10, 2, "Small L-holder"),
      ],
      [Tools.OTHER.BREAKER_BARS]: [
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 2, "Large handle holder"),
      ],
      [Tools.OTHER.EXTENSIONS]: [
        new DrawerProProduct("DPRO-HH-S-10PC", 10, 2, "Small handle holder"),
      ],
      [Tools.OTHER.SMALL_PICKS]: [
        new DrawerProProduct("DPRO-HH-S-10PC", 10, 2, "Small handle holder"),
      ],
      [Tools.OTHER.LARGE_PICKS]: [
        new DrawerProProduct("DPRO-HH-S-10PC", 10, 1, "Small handle holder"),
        new DrawerProProduct("DPRO-HH-L-10PC", 10, 1, "Large handle holder"),
      ],
    };

    this.#StepNameMap = {
      "select-tools": "Select your tools",
      "select-colours": "Select your colors",
      wrenches: "Wrench organization",
      ratchets: "Ratchet organization",
      "breakerbars-and-extensions": "Breaker bar/extension organization",
      "torque-wrenches": "Torque wrench organization",
      screwdrivers: "Screwdriver organization",
      "picks-and-hooks": "Picks and hooks organization",
      "review-page": "Confirmation",
    };

    this.EventHandlers = {
      NAV_BUTTONS: {
        CLOSE_BUTTON: ConfiguratorEventHandler.METHODS.NAV_BUTTONS.CLOSE_BUTTON,
        BACK_BUTTON:
          ConfiguratorEventHandler.METHODS.NAV_BUTTONS.BACK_BUTTON.bind(
            null,
            this,
          ),
        NEXT_BUTTON:
          ConfiguratorEventHandler.METHODS.NAV_BUTTONS.NEXT_BUTTON.bind(
            null,
            this,
          ),
        ATC_BUTTON:
          ConfiguratorEventHandler.METHODS.NAV_BUTTONS.ATC_BUTTON.bind(
            null,
            this,
          ),
      },
      QTY_SELECTORS: {
        DECREMENT: (buttonEl) => () =>
          ConfiguratorEventHandler.METHODS.QTY_SELECTORS.DECREMENT(
            this,
            buttonEl,
          ),
        INCREMENT: (buttonEl) => () =>
          ConfiguratorEventHandler.METHODS.QTY_SELECTORS.INCREMENT(
            this,
            buttonEl,
          ),
        INPUT_FOCUSOUT: (inputEl) => () =>
          ConfiguratorEventHandler.METHODS.QTY_SELECTORS.INPUT_FOCUSOUT(
            this,
            inputEl,
          ),
      },
      TOOL_SELECTIONS: {
        TOOL_SELECTION_BOX: (toolSelectionBox) => () =>
          ConfiguratorEventHandler.METHODS.TOOL_SELECTIONS.TOOL_SELECTION_BOX(
            this,
            toolSelectionBox,
          ),
        SELECT_ALL_TOOLS:
          ConfiguratorEventHandler.METHODS.TOOL_SELECTIONS.SELECT_ALL_TOOLS.bind(
            null,
            this,
          ),
      },
      GLIDE: {
        RUN_AFTER: ConfiguratorEventHandler.METHODS.GLIDE.RUN_AFTER.bind(
          null,
          this,
        ),
      },
      COLOUR_SWATCHES: (swatchEl) =>
        ConfiguratorEventHandler.METHODS.COLOUR_SWATCHES(this, swatchEl),
      MODALS: {
        TRANSFER_MODAL_TRIGGER:
          ConfiguratorEventHandler.METHODS.MODALS.TRANSFER_MODAL_TRIGGER.bind(
            null,
            this,
          ),
      },
    };

    if (this.URLParams.get("dvc-xfer")) {
      // If we detect that a session was transferred to another device, then push the appropriate event
      window.dataLayer.push({
        event: "drawerpro_device_transfer",
      });
    }

    if (
      localStorage.getItem("drpConfiguratorState") ||
      this.URLParams.get("state")
    ) {
      this.loadState();
    } else {
      window.dataLayer.push({
        event: "start_drawerpro_configurator",
      });

      this.Glide.on("mount.after", () => {
        document.querySelector(".drp__opaque-overlay").style.display = "none";
      });

      this.Glide.mount();
      this.NumSteps = this.Glide._c.Sizes.length;
    }
  }

  /**
   * Handles initialization of states for the configurator.
   *
   * @param {function[]} exclusions=[] - Array of class methods to exclude from the init call.
   * @returns {void}
   */
  __init = (exclusions = []) => {
    if (!exclusions.includes(this.__refreshNavBtnStates))
      this.__refreshNavBtnStates();
    if (!exclusions.includes(this.__initToolSelections))
      this.__initToolSelections();
    if (!exclusions.includes(this.__initQtySelectors))
      this.__initQtySelectors();
    if (!exclusions.includes(this.__initGlideEventHandlers))
      this.__initGlideEventHandlers();
    if (!exclusions.includes(this.__initBtnHandling)) this.__initBtnHandling();
    if (!exclusions.includes(this.__initColourSwatches))
      this.__initColourSwatches();
    if (!exclusions.includes(this.__initModalEventHandlers))
      this.__initModalEventHandlers();
    if (!exclusions.includes(this.__initHolderImages))
      this.__initHolderImages();

    if (Object.keys(this.currentStep).length === 0 || !this.currentStep) {
      this.currentStep = {
        index: 0,
        stepName: "select-tools",
        stepTitle: this.#StepNameMap["select-tools"],
      };
    }

    this.updateStepLabel();
  };

  /**
   * Filters out all holder images in imgNodeList that do not match the "colour"
   * parameter.
   *
   * @param {NodeList} imgNodeList - nodelist of holder images
   * @param {string} colour        - "blue" or "red"
   *
   * @returns {void}
   */
  __filterHolderImages = (imgNodeList, colour) => {
    imgNodeList.forEach((image) => {
      if (image.dataset.holderColour != colour) {
        image.style.display = "none";
      } else {
        image.style.display = "flex";
      }
    });
  };

  /**
   * Container method that houses all of the logic for the conditional rendering of
   * the images with coloured holders.
   *
   * Images with coloured holders are conditionally rendered depending on the user's
   * selection during the colour selection step of the configurator.
   *
   * @returns {void}
   */
  __initHolderImages = () => {
    // first, reset all image states
    this.Selectors.HolderImagesAll.forEach(
      (image) => (image.style.display = "flex"),
    );

    if (this.colourSelections.metric) {
      this.__filterHolderImages(
        this.Selectors.HolderImagesMetric,
        this.colourSelections.metric,
      );
    }

    if (this.colourSelections.sae) {
      this.__filterHolderImages(
        this.Selectors.HolderImagesSAE,
        this.colourSelections.sae,
      );
    }

    if (this.colourSelections.other) {
      this.__filterHolderImages(
        this.Selectors.HolderImagesOther,
        this.colourSelections.other,
      );
    }
  };

  /**
   * Intializes click event listeners for the "Back", "Next Step", and "Add All to Cart" buttons.
   *
   * @returns {void}
   */
  __initBtnHandling = () => {
    this.__removeAllClickHandlers(this.Selectors.BackBtn);
    this.__removeAllClickHandlers(this.Selectors.NextBtn);
    this.__removeAllClickHandlers(this.Selectors.AtcBtn);

    this.Selectors = this.__refreshSelectors();

    this.Selectors.BackBtn.addEventListener(
      "click",
      this.EventHandlers.NAV_BUTTONS.BACK_BUTTON,
    );
    this.Selectors.NextBtn.addEventListener(
      "click",
      this.EventHandlers.NAV_BUTTONS.NEXT_BUTTON,
    );
    this.Selectors.AtcBtn.addEventListener(
      "click",
      this.EventHandlers.NAV_BUTTONS.ATC_BUTTON,
    );
    this.Selectors.CloseBtn.addEventListener(
      "click",
      this.EventHandlers.NAV_BUTTONS.CLOSE_BUTTON,
    );
  };

  /**
   * Handles the behaviour of what happens on the first step when the user is selecting
   * all of the tools that they want to organize.
   *
   * @returns {void}
   */
  __initToolSelections = () => {
    this.Selectors.ToolSelections.forEach((toolSelectionBox) =>
      this.__removeAllClickHandlers(toolSelectionBox),
    );
    this.__removeAllClickHandlers(this.Selectors.SelectAllTools);

    this.Selectors = this.__refreshSelectors();

    this.Selectors.ToolSelections.forEach((toolSelectionBox) =>
      toolSelectionBox.addEventListener(
        "click",
        this.EventHandlers.TOOL_SELECTIONS.TOOL_SELECTION_BOX(toolSelectionBox),
      ),
    );

    this.Selectors.SelectAllTools.addEventListener(
      "click",
      this.EventHandlers.TOOL_SELECTIONS.SELECT_ALL_TOOLS,
    );
  };

  /**
   * Intializes the behaviour of everything related to quantity selectors in the configurator.
   *
   *    - Clamping min/max values
   *    - Increment button click handling
   *    - Decrement button click handling
   *
   * @returns {void}
   */
  __initQtySelectors() {
    this.Selectors.QtyDecrements.forEach((decBtn) =>
      this.__removeAllClickHandlers(decBtn),
    );
    this.Selectors.QtyIncrements.forEach((incBtn) =>
      this.__removeAllClickHandlers(incBtn),
    );
    this.Selectors.QtyInputs.forEach((input) =>
      this.__removeAllClickHandlers(input),
    );

    this.Selectors = this.__refreshSelectors();

    // Decrement button click handling
    this.Selectors.QtyDecrements.forEach((decBtn) =>
      decBtn.addEventListener(
        "click",
        this.EventHandlers.QTY_SELECTORS.DECREMENT(decBtn),
      ),
    );

    // Increment button click handling
    this.Selectors.QtyIncrements.forEach((incBtn) =>
      incBtn.addEventListener(
        "click",
        this.EventHandlers.QTY_SELECTORS.INCREMENT(incBtn),
      ),
    );

    // Clamping min/max values
    this.Selectors.QtyInputs.forEach((input) =>
      input.addEventListener(
        "focusout",
        this.EventHandlers.QTY_SELECTORS.INPUT_FOCUSOUT(input),
      ),
    );
  }

  /**
   * Handles the behaviour of colour swatch selections on the step where the user has to select their
   * prefered colours for their clips.
   *
   * @returns {void}
   */
  __initColourSwatches = () => {
    this.Selectors.ColourSwatches.forEach((swatch) =>
      swatch.addEventListener(
        "click",
        this.EventHandlers.COLOUR_SWATCHES.bind(null, swatch),
      ),
    );
  };

  /**
   * Handles all of the logic for event handling of the Glide object.
   *
   * - run.after: custom event handler to dynamically keep track of the current step in the this.currentStep object.
   *              code runs after the transition effect of the slide has occurred.
   *
   * @returns {void}
   */
  __initGlideEventHandlers = () => {
    this.Glide.on("run.after", this.EventHandlers.GLIDE.RUN_AFTER);
  };

  __initModalEventHandlers = () => {
    this.Selectors.TransferModalTrigger.addEventListener(
      "click",
      this.EventHandlers.MODALS.TRANSFER_MODAL_TRIGGER,
    );
  };

  /**
   * Refreshes the state of certain elements in the configurator:
   *
   *  - Back button
   *  - Next step button
   *  - this.toolsSelected property
   *
   * @returns {void}
   */
  __refreshNavBtnStates() {
    // Reset state of tool selection flag
    this.toolsSelected = this.checkToolSelection();
    // Disable the back button if we're on the first step.
    this.Selectors.BackBtn.disabled = this.Glide.index === 0;

    // Disable the next step button if nothing is selected. - toggle comment for testing
    // this.Selectors.NextBtn.disabled = !this.toolsSelected;
  }

  /**
   * Returns a dynamically refreshed object of our standard selectors for each DrawerPro instance.
   *
   * @returns {object} - JSON of all of our standard selectors
   */
  __refreshSelectors = () => ({
    StepLabel: document.querySelector(".drp__step-label"),
    ProgressBar: document.querySelector(".drp__progress-bar"),
    CloseBtn: document.getElementById("drp__exit"),
    BackBtn: document.querySelector(".drp__btn--back"),
    NextBtn: document.querySelector(".drp__btn--next"),
    AtcBtn: document.querySelector(".drp__btn--atc"),
    SidePanel: document.querySelector(".sidepanel"),
    MainBody: document.querySelector(".body__main"),
    ToolSelections: document.querySelectorAll(".ts__box--with-img"),
    SelectAllTools: document.querySelector(".ts__all-tools"),
    StepTrack: document.querySelector(".drp__body .glide__slides"),
    StepPanels: document.querySelectorAll(".drp__body .glide__slide"),
    QtyInputs: document.querySelectorAll(".qty-selector__input"),
    QtyDecrements: document.querySelectorAll(".qty-selector__minus"),
    QtyIncrements: document.querySelectorAll(".qty-selector__plus"),
    ColourSwatches: document.querySelectorAll(".cs__swatch"),
    MetricColourSelection: document.querySelector("#metric-clip-colour"),
    SAEColourSelection: document.querySelector("#sae-clip-colour"),
    OtherColourSelection: document.querySelector("#other-clip-colour"),
    ProductCards: document.querySelectorAll(".drp__product-card"),
    TransferModalTrigger: document.querySelector(
      ".drp__icon--phone.modal-trigger",
    ),
    QRCodeCanvas: document.getElementById("drp__qr-code"),
    SidePanelHeader: document.querySelector(".sidepanel__header"),
    SidePanelBody: document.querySelector(".sidepanel__body"),
    HolderImagesAll: document.querySelectorAll(".drp-colour-img"),
    HolderImagesMetric: document.querySelectorAll(
      ".drp-colour-img[data-tool-type=metric]",
    ),
    HolderImagesSAE: document.querySelectorAll(
      ".drp-colour-img[data-tool-type=sae]",
    ),
    HolderImagesOther: document.querySelectorAll(
      ".drp-colour-img[data-tool-type=other]",
    ),
    ColourSelectionImageWrapper: document.querySelector(
      ".drp__selection-img-container",
    ),
    MobileTotalValue: document.getElementById("mobile-total-value"),
  });

  /**
   * Removes all event handlers in existence for the given DOM node.
   * Its purpose is for getting rid of duplicate event handlers that fire multiple times
   * on a single trigger.
   *
   * @param {node} - DOM node object
   * @returns {node}
   */
  __removeAllClickHandlers = (node) => {
    // Clone the node to remove all event listeners
    const newNode = node.cloneNode(true);

    // Replace the original node with the cloned node
    node.parentNode.replaceChild(newNode, node);

    return newNode; // Return the new node in case further operations are needed
  };

  /**
   * Handles the toggling of the tool selection boxes in the first step of the configurator.
   *
   * @param {any} toolSelectionEl - Tool selection box element.
   * @param {any} selectAllFlag=false - Flag to tell the method whether or not to select all.
   * @returns {void}
   */
  toggleToolSelection = (toolSelectionEl, selectAllFlag = false) => {
    if (!selectAllFlag) {
      toolSelectionEl?.classList?.toggle("active");
    } else if (
      toolSelectionEl?.className?.includes("active") &&
      selectAllFlag
    ) {
      return;
    } else {
      toolSelectionEl?.classList?.add("active");
    }
  };

  /**
   * Assesses whether or not selections have been made within the current step.
   *
   * TODO: make it smarter by mapping the step to the glide slide index to make sure that we're
   * checking the current step.
   *
   * @returns {boolean} - true if any sort of selection has been made, false otherwise.
   */
  checkToolSelection = () => {
    // if no qty exists in qty inputs or if no tools are selected, return false; else true
    return (
      ([...this.Selectors.ToolSelections].filter((e) =>
        e.className.includes("active"),
      ).length > 0 &&
        this.Glide.index === 0) ||
      [
        ...document.querySelectorAll(
          `.drp-step[data-step-id='${this.currentStep?.stepName}'] .qty-selector__input`,
        ),
      ].filter((qtyInput) => qtyInput?.value > 0).length > 0 ||
      !!(
        this.currentStep?.stepName == "select-colours" &&
        this.colourSelections.metric &&
        this.colourSelections.sae &&
        this.colourSelections.other
      )
    );
  };

  /**
   * This function fires every time the "next step" button is clicked. Its purpose is to process
   * all of the qty selectors on the current step and translate it into objects to push into the
   * CartList object. The CartList updates after every step and on the final step, it is used
   * to render all of the product cards for the user to review before adding everything to the cart.
   *
   * @returns {void}
   */
  processToolSelectionToCartList = () => {
    // create OR reset entry in CartList for the current step's SKU selections
    this.CartList[this.currentStep.stepName] = [];

    // get current step and all of the qty selectors >0 in value within that step
    const validQtySelectors = [
      ...document.querySelectorAll(
        ".glide__slide--active .qty-selector__input",
      ),
    ]
      .filter((input) => input.value > 0)
      .forEach((input) => {
        // for each qty selector, read the data-tool-id attribute and retrieve the holder type from the this.#ToolHolderTypeMap property
        const toolId = input?.dataset?.toolId;
        const toolType = this.#ToolHolderTypeMap[toolId]; // with the holder type, we can find out the colour we need from this.ToolHolderMap
        const colour = this.colourSelections[toolType];
        const qty = parseInt(input.value);
        const boundInputId = input.id;

        // Add an entry for all DrawerProProducts to the CartList object
        this.ToolHolderMap[toolId].forEach((dProProduct) => {
          this.CartList[this.currentStep.stepName].push(
            dProProduct.getProduct(colour, qty, boundInputId),
          );
        });
      });

    // group all entries of the array in the newly created key of CartList by sku, qtyPerPack, and piecesPerTool
    this.CartList[this.currentStep.stepName] = this.aggregateBySkuAndProperties(
      this.CartList[this.currentStep.stepName],
    );

    this.State.set("holderSelections", this.CartList);
  };

  /**
   * Ingests an array of DrawerProProduct instances and groups them by sku, piecesPerTool, and qtyPerPack
   *
   * @param {DrawerProProduct[]} data - object of arrays containing DrawerProProduct instances
   * @returns {array}
   */
  aggregateBySkuAndProperties = (data) => {
    // Create an object to store aggregated results
    const aggregated = {};

    // Loop through each item in the data
    data.forEach((item) => {
      const {
        sku,
        qtyPerPack,
        piecesPerTool,
        toolQty,
        packQty,
        title,
        option,
        variantId,
        pricePerPack,
        boundInputId,
      } = item;

      // Create a unique key based on sku, qtyPerPack, and piecesPerTool
      const key = `${sku}-${qtyPerPack}-${piecesPerTool}-${boundInputId}`;

      // If the key already exists, add the toolQty
      if (aggregated[key]) {
        aggregated[key].toolQty += toolQty;
        aggregated[key].packQty += packQty;
      } else {
        // Otherwise, initialize the entry in the aggregated object
        aggregated[key] = {
          sku,
          qtyPerPack,
          piecesPerTool,
          toolQty,
          packQty,
          title,
          option,
          variantId,
          pricePerPack,
          boundInputId,
        };
      }
    });

    // Convert the aggregated object back into an array
    return Object.values(aggregated);
  };

  /**
   * Ingests the CartList property object schema and outputs an object with the SKUs as the keys
   * and each keys' value being that SKU's total aggregated (sum) pack quantity, the title, and
   * the variant id.
   *
   * @param {object} cartList - this should always be the CartList property object in its defined schema
   * @returns {object}
   */
  aggregateCartList = (cartList) => {
    const result = {};

    console.log(cartList);

    // Loop through each category in the object
    Object.keys(cartList).forEach((category) => {
      cartList[category].forEach((item) => {
        const {
          sku,
          toolQty,
          piecesPerTool,
          qtyPerPack,
          packQty,
          title,
          variantId,
          pricePerPack,
        } = item;

        // If the SKU already exists in the result, aggregate the packQty
        if (result[sku]) {
          result[sku].toolQty += toolQty;
          result[sku].packQty = Math.ceil(
            (result[sku].toolQty * piecesPerTool) / qtyPerPack,
          );
          //result[sku].packQty += packQty;
        } else {
          // Otherwise, initialize the SKU in the result
          result[sku] = {
            packQty: packQty,
            title: title,
            variantId: variantId,
            pricePerPack: pricePerPack,
            toolQty: toolQty,
            piecesPerTool: piecesPerTool,
            qtyPerPack: qtyPerPack,
          };
        }
      });
    });

    return result;
  };

  /**
   * Filters out the step panels (glide slides) that we don't need, based on the user's
   * tool selection.
   *
   * @returns {void}
   */
  filterSteps = (providedState = false, selectedTools = []) => {
    if (!providedState && selectedTools.length === 0) {
      selectedTools = [...this.Selectors.ToolSelections]
        .filter((toolSelection) => toolSelection?.className?.includes("active"))
        .map((toolSelection) => toolSelection?.dataset?.stepId);

      selectedTools.push("select-tools");
      selectedTools.push("select-colours");
      selectedTools.push("review-page");
    }

    this.currentStep.selections = {};
    this.Selectors.SidePanelBody.innerHTML = "";

    this.Glide.destroy();

    // Replace the Glide track with the original state.
    this.Selectors.StepTrack.innerHTML = this.#InitialStepState.innerHTML;
    this.Selectors = this.__refreshSelectors();

    // cross-reference selected tools against data-step-ids for the actual slides/panels in the
    // configurator and filter against that.
    this.Selectors.StepPanels.forEach((stepPanel) => {
      const shouldShow = selectedTools.includes(stepPanel?.dataset?.stepId);

      if (!shouldShow) {
        stepPanel.parentNode.remove();
        stepPanel.remove();
      }
    });

    // Update the step map with the newly filtered steps
    this.Selectors = this.__refreshSelectors();
    this.StepMap = [...this.Selectors.StepPanels].map(
      (stepPanel) => stepPanel?.dataset?.stepId,
    );
    this.NumSteps = this.StepMap.length;
    this.State.set("selectedTools", this.StepMap);

    // Rebuild Glide
    this.Glide = new Glide(".body__main", {
      type: "slider",
      rewind: false,
      gap: 0,
      peek: 0,
      keyboard: false,
      swipeThreshold: false,
      dragThreshold: false,
      animationDuration: 200,
    });

    this.Glide.on("mount.after", () => {
      document.querySelector(".drp__opaque-overlay").style.display = "none";
    });

    if (providedState) {
      // Reset the state of the colour selections
      this.colourSelections = {
        metric: this.State.get().colourSelections.metric,
        sae: this.State.get().colourSelections.sae,
        other: this.State.get().colourSelections.other,
      };
    } else {
      // Re-init the event handler for "run.after" on the Glide instance
      this.__initGlideEventHandlers();
    }

    this.Glide.mount();

    // Re-do the states of all the selected tools in the first step
    this.Selectors.ToolSelections.forEach((toolSelection) =>
      selectedTools.includes(toolSelection?.dataset?.stepId)
        ? toolSelection.classList.add("active")
        : null,
    );

    // Re-initialize all of the event handlers.
    this.__init([
      //this.__initToolSelections,
      this.__initGlideEventHandlers,
      //this.__initQtySelectors
    ]);

    if (!providedState) {
      this.colourSelections = {
        metric: null,
        sae: null,
        other: null,
      };
      this.Glide.go(">");
    } else {
      this.Glide.go(`=${this.State.get().currentStep.index}`);
    }
  };

  filterProductCards = (aggregatedCartList) => {
    for (const sku in aggregatedCartList) {
      const { variantId, packQty } = aggregatedCartList[sku];

      console.log(variantId, packQty);

      this.Selectors.ProductCards.forEach((productCard) => {
        if (
          productCard?.dataset?.drpSku == sku &&
          productCard?.dataset?.drpVariantId == variantId
        ) {
          productCard.style.display = "flex";
          productCard.querySelector(".qty-selector__input").value = packQty;
        }
      });
    }
  };

  /**
   * Updates the "Step N - StepName" label in the header of the configurator.
   *
   * @returns {void}
   */
  updateStepLabel = () => {
    this.Selectors.StepLabel.innerText = `Step ${this.currentStep.index + 1} - ${this.#StepNameMap[this.currentStep.stepName]}`;
  };

  /**
   * Dynamically re-renders the slideout cart contents and body in the DOM.
   *
   * @returns {void}
   */
  updateCartContent = () => {
    fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((responseJSON) => {
        var parser = new DOMParser();
        // Parse the HTML string
        var newDrawerFromServer = parser
          .parseFromString(responseJSON["cart-drawer"], "text/html")
          .querySelector("cart-drawer");
        const oldDrawer = document.querySelector("cart-drawer");

        oldDrawer.replaceWith(newDrawerFromServer);
        newDrawerFromServer.open();

        var newIconBubbleFromServer = parser.parseFromString(
          responseJSON["cart-icon-bubble"],
          "text/html",
        );
        document.querySelector("#cart-icon-bubble").innerHTML =
          newIconBubbleFromServer.getElementById(
            "shopify-section-cart-icon-bubble",
          ).innerHTML;
      });
  };

  /**
   * Updates the total value of the cart based on selections made on
   * the review page.
   *
   * @param {object} aggregatedCartList - cartList object grouped by SKU
   *
   * @returns {any}
   */
  __updateTotalValue = () => {
    const total = parseFloat(
      Math.round(
        [...this.Selectors.ProductCards].reduce((acc, card) => {
          const qty = card.querySelector(".qty-selector__input").value;
          const price = card
            .querySelector(".product-card__price")
            .innerText.match(/[-+]?\d{1,3}(?:,\d{3})*(?:\.\d+)?/g)?.[0];
          const total = qty * price;
          return acc + total;
        }, 0) * 100,
      ) / 100,
    ).toFixed(2);

    this.Selectors.SidePanelBody.innerHTML = `<span class='sidepanel__total-price'>$${total}</span>`;
    this.Selectors.MobileTotalValue.innerHTML = `$${total}`;
  };

  __updateProductCardTotalValue = (productCard) => {
    const price = productCard
      .querySelector(".product-card__price")
      .innerText.match(/[-+]?\d{1,3}(?:,\d{3})*(?:\.\d+)?/g)?.[0];
    const qty = productCard.querySelector(".qty-selector__input").value;
    const total = parseFloat(Math.round(price * qty * 100) / 100);

    productCard.querySelector(".product-card__total_price").innerText =
      `$${total}`;
  };

  /**
   * Method that handles all of the behaviour exactly when landing on the review page (final step) of the
   * configurator.
   *
   * @returns {void}
   */
  reviewPageHandler = () => {
    let aggregatedCartList = this.aggregateCartList(this.CartList);

    console.log(aggregatedCartList);

    this.filterProductCards(aggregatedCartList);
    this.Selectors.AtcBtn.style.display = "block";
    this.Selectors.NextBtn.style.display = "none";
    this.Selectors.SidePanelHeader.innerText = "Total Value";

    this.__updateTotalValue();

    // render warnings/errors
    this.Selectors.ProductCards.forEach((card) => {
      const qty = parseInt(card?.dataset?.drpInventoryQuantity);
      const inputValue = parseInt(
        card.querySelector(".qty-selector__input").value,
      );

      if (qty === 0) {
        card.querySelector(".product-card__warning-wrapper").style.display =
          "none";
        card.querySelector(".product-card__icon-wrapper").style.display =
          "flex";
        card.querySelector(".product-card__error-wrapper").style.display =
          "flex";
      } else if (qty > 0 && inputValue > qty) {
        card.querySelector(".product-card__error-wrapper").style.display =
          "none";
        card.querySelector(".product-card__icon-wrapper").style.display =
          "flex";
        card.querySelector(".product-card__warning-wrapper").style.display =
          "flex";

        card.querySelector(".product-card__warning-text").innerText =
          `Only ${qty} available.`;
      }

      this.__updateProductCardTotalValue(card);
    });

    // add click event handlers for only the qty selectors in the review page to dynamically account for ATC warnings

    // decrement buttons
    document
      .querySelectorAll(".drp__product-card .qty-selector__minus")
      .forEach((decBtn) => {
        decBtn.addEventListener("click", () => {
          const associatedCard =
            decBtn.parentNode.parentNode.parentNode.parentNode.parentNode
              .parentNode;
          const associatedInput = document.querySelector(
            `input#${decBtn?.dataset?.inputLink}`,
          );
          const availableQty = parseInt(
            associatedCard.dataset.drpInventoryQuantity,
          );

          if (availableQty === 0) {
            associatedCard.querySelector(
              ".product-card__warning-wrapper",
            ).style.display = "none";
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "flex";
            associatedCard.querySelector(
              ".product-card__error-wrapper",
            ).style.display = "flex";
          } else if (
            availableQty > 0 &&
            associatedInput.value >= availableQty
          ) {
            associatedCard.querySelector(
              ".product-card__error-wrapper",
            ).style.display = "none";
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "flex";
            associatedCard.querySelector(
              ".product-card__warning-wrapper",
            ).style.display = "flex";

            associatedCard.querySelector(
              ".product-card__warning-text",
            ).innerText = `Only ${availableQty} available.`;
          } else {
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "none";
          }

          this.__updateTotalValue();
          this.__updateProductCardTotalValue(associatedCard);
        });
      });

    // increment buttons
    document
      .querySelectorAll(".drp__product-card .qty-selector__plus")
      .forEach((incBtn) => {
        incBtn.addEventListener("click", () => {
          const associatedCard =
            incBtn.parentNode.parentNode.parentNode.parentNode.parentNode
              .parentNode;
          const associatedInput = document.querySelector(
            `input#${incBtn?.dataset?.inputLink}`,
          );
          const availableQty = parseInt(
            associatedCard.dataset.drpInventoryQuantity,
          );

          if (availableQty === 0) {
            associatedCard.querySelector(
              ".product-card__warning-wrapper",
            ).style.display = "none";
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "flex";
            associatedCard.querySelector(
              ".product-card__error-wrapper",
            ).style.display = "flex";
          } else if (
            availableQty > 0 &&
            associatedInput.value >= availableQty
          ) {
            associatedCard.querySelector(
              ".product-card__error-wrapper",
            ).style.display = "none";
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "flex";
            associatedCard.querySelector(
              ".product-card__warning-wrapper",
            ).style.display = "flex";

            associatedCard.querySelector(
              ".product-card__warning-text",
            ).innerText = `Only ${availableQty} available.`;
          } else {
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "none";
          }

          this.__updateTotalValue();
          this.__updateProductCardTotalValue(associatedCard);
        });
      });

    // inputs
    document
      .querySelectorAll(".drp__product-card .qty-selector__input")
      .forEach((incBtn) => {
        incBtn.addEventListener("focusout", () => {
          const associatedCard =
            incBtn.parentNode.parentNode.parentNode.parentNode.parentNode
              .parentNode;
          const availableQty = parseInt(
            associatedCard.dataset.drpInventoryQuantity,
          );
          const inputValue = incBtn.value;

          if (availableQty === 0) {
            associatedCard.querySelector(
              ".product-card__warning-wrapper",
            ).style.display = "none";
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "flex";
            associatedCard.querySelector(
              ".product-card__error-wrapper",
            ).style.display = "flex";
          } else if (availableQty > 0 && inputValue >= availableQty) {
            associatedCard.querySelector(
              ".product-card__error-wrapper",
            ).style.display = "none";
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "flex";
            associatedCard.querySelector(
              ".product-card__warning-wrapper",
            ).style.display = "flex";

            associatedCard.querySelector(
              ".product-card__warning-text",
            ).innerText = `Only ${availableQty} available.`;
          } else {
            associatedCard.querySelector(
              ".product-card__icon-wrapper",
            ).style.display = "none";
          }

          this.__updateTotalValue();
          this.__updateProductCardTotalValue(associatedCard);
        });
      });
  };

  /**
   * Resets the states of all product cards on the review page for the purpose of recalculating
   * the quantities and selected SKUs when users go back from the review page and then back TO
   * the review page.
   *
   * Also hides the ATC button from the footer.
   *
   * @returns {void}
   */
  resetReviewPage = () =>
    this.Selectors.ProductCards.forEach((productCard) => {
      this.Selectors.AtcBtn.style.display = "none";
      this.Selectors.NextBtn.style.display = "block";
      productCard.style.display = "none";
      productCard.querySelector(".qty-selector__input").value = 0;
    });

  saveState = () =>
    localStorage.setItem("drpConfiguratorState", this.State.encode());

  loadState = () => {
    const state = this.URLParams.get("state")
      ? this.State.decodeHashedState(this.URLParams.get("state"))
      : this.State.decode(localStorage.getItem("drpConfiguratorState"));
    const { colourSelections, selectedTools, holderSelections, currentStep } =
      state;

    this.State.set("colourSelections", colourSelections);
    this.State.set("selectedTools", selectedTools);
    this.State.set("holderSelections", holderSelections);
    this.State.set("currentStep", currentStep);

    this.colourSelections = colourSelections;

    // Set current step
    this.currentStep = state.currentStep;

    // Filter the step panels
    this.filterSteps(true, state.selectedTools);

    // Fill in all colour selections
    if (state.colourSelections.metric) {
      document.getElementById(
        `Swatch--metric-clip-colour--${state.colourSelections.metric}`,
      ).checked = true;
      this.Selectors.MetricColourSelection.value =
        state.colourSelections.metric;
    }
    if (state.colourSelections.sae) {
      document.getElementById(
        `Swatch--sae-clip-colour--${state.colourSelections.sae}`,
      ).checked = true;
      this.Selectors.SAEColourSelection.value = state.colourSelections.sae;
    }
    if (state.colourSelections.other) {
      document.getElementById(
        `Swatch--other-clip-colour--${state.colourSelections.other}`,
      ).checked = true;
      this.Selectors.OtherColourSelection.value = state.colourSelections.other;
    }

    this.Selectors.MetricColourSelection = state.colourSelections.metric;
    this.Selectors.SAEColourSelection = state.colourSelections.sae;
    this.Selectors.OtherColourSelection = state.colourSelections.other;

    // Fill in all holder selections
    if (
      state.holderSelections &&
      Object.keys(state.holderSelections).length > 0
    ) {
      for (const pageName in state.holderSelections) {
        state.holderSelections[pageName].forEach(
          (drpProduct) =>
            (document.getElementById(drpProduct.boundInputId).value =
              drpProduct?.toolQty || 0),
        );
      }
    }

    // Populate the sidebar with all of the current selections
    this.populateSelectionBox();

    // Filter out review page step
    if (state.currentStep?.stepName === "review-page") {
      this.CartList = state?.holderSelections;
      this.reviewPageHandler();
    }
    if (this.currentStep.stepName === "select-colours") {
      this.colourSelectionHandler();
    }
  };

  /**
   * Generates a hashed URL of the state of the configurator. The purpose of this is to
   * allow for session transfers between different devices.
   *
   * @param {ConfiguratorState} State
   * @returns {String}
   */
  generateHashedURL = (State) =>
    window.location.href +
    "?state=" +
    State.hashState(JSON.stringify(State.get())) +
    "&dvc-xfer=1";

  validateInteger = (int) => Number.isInteger(parseInt(int));

  /**
   * Returns the label tag to put in the selection box body.
   *
   * @param {Node} qtySelector
   *
   * @returns {string} - label to put in the selection box body
   */
  getQtySelectorLabel = (qtySelector) => {
    const labelTag =
      qtySelector.parentNode.parentNode.parentNode.firstElementChild
        .innerText || null;
    const selectionMatrixTag =
      qtySelector.parentNode.parentNode.parentNode.parentNode.firstElementChild
        .innerText || null;

    if (labelTag.includes("\n+") || !labelTag) {
      return selectionMatrixTag;
    } else {
      return labelTag;
    }
  };

  /**
   * Given a toolId and qty, returns an array of human-readable strings that tells us
   * how many of each DrawerPro clip are required.
   *
   * @param {any} toolId - toolId of the holder type in #ToolHolderTypeMap
   * @param {any} qty    - qty of toolId
   *
   * @returns {object} holderInfo - qty for the holder selection + array of strings of required holders based on the toolId
   */
  processCurrentStepSelections = (toolId, qty) => {
    const toolType = this.#ToolHolderTypeMap[toolId];
    const colour = this.colourSelections[toolType];

    // Add an entry for all DrawerProProducts to the stack
    let holderInfo = {
      toolQty: qty,
      holderDescriptions: [],
    };

    this.ToolHolderMap[toolId].forEach((dProProduct) => {
      let product = dProProduct.getProduct(colour, qty);
      holderInfo.holderDescriptions.push(
        `${product.piecesPerTool}x ${product.description}`,
      );
    });

    return holderInfo;
  };

  /**
   * Updates the currentStep.selections object to reflect what the user has selected in the step's inputs.
   *
   * @param {any} key                   - key of the holder name in currentStep.selections
   * @param {object} holderDescriptions - ingests the output from processCurrentStepSelections()
   *
   * @returns {object}
   */
  updateCurrentStepSelectionsObj = (key, holderDescriptions) => {
    if (!this.currentStep.selections) {
      this.currentStep.selections = {};
    }
    this.currentStep.selections[key] = holderDescriptions;
    return this.currentStep.selections;
  };

  /**
   * Updates the selections sidebar in the DOM based on the contents of
   * currentStep.selection.
   *
   * @returns {void}
   */
  pushCurrentStepSelections = () => {
    const selections = this.currentStep.selections;
    this.Selectors.SidePanelBody.innerHTML = "";

    let index = 0;

    for (const key in selections) {
      if (selections[key]?.toolQty > 0) {
        this.Selectors.SidePanelBody.innerHTML += `
          <span>${selections[key]?.toolQty || 0}x ${key}</span>
  
          <span id="holder-desc-${index}" class="sidepanel__holder-description">
            <span class="sidepanel__holder-requires">Each one requires: </span>
            <ul class="sidepanel__holder-list"></ul>
          </span>
        `;

        selections[key].holderDescriptions.forEach((holderDescription) => {
          document.querySelector(
            `#holder-desc-${index} .sidepanel__holder-list`,
          ).innerHTML += `
            <li class="sidepanel__holder-entry">
              ${holderDescription}
            </li>
          `;
        });
      }

      index++;
    }
  };

  /**
   * Populates the selection box body appropriately based on the values of the current page's
   * qty inputs.
   *
   * @returns {void}
   */
  populateSelectionBox = () => {
    if (
      this.currentStep.stepName != "review-page" &&
      this.currentStep.stepName != "select-tools" &&
      this.currentStep.stepName != "select-colours"
    ) {
      document
        .querySelectorAll(
          `[data-step-id="${this.currentStep.stepName}"] .qty-selector__input`,
        )
        .forEach((inputEl) => {
          const label = this.getQtySelectorLabel(inputEl);
          const values = this.processCurrentStepSelections(
            inputEl.dataset.toolId,
            parseInt(inputEl.value),
          );

          this.updateCurrentStepSelectionsObj(label, values);
          this.pushCurrentStepSelections();
        });
    }
  };

  /**
   * Sets the display state (visibility) of the selection box on the right side based on
   * the step that the user is on.
   *
   * @returns {void}
   */
  setSelectionBoxState = () => {
    if (
      this.currentStep.stepName == "select-tools" ||
      this.currentStep.stepName == "select-colours"
    ) {
      this.Selectors.SidePanel.style.display = "none";
    } else {
      this.Selectors.SidePanel.style.display = "flex";

      if (this.currentStep.stepName != "review-page") {
        this.Selectors.SidePanelHeader.innerText = "Selections";
      }
    }
  };

  colourSelectionHandler = () => {
    const colourDict = {
      blue: "B",
      red: "R",
    };

    let colourMap = "";

    for (const type in this.colourSelections) {
      if (this.colourSelections[type]) {
        colourMap += colourDict[this.colourSelections[type]];
      } else {
        // if null, append "G" for grey
        colourMap += "G";
      }
    }

    // Reset all images before doing anything
    this.Selectors.ColourSelectionImageWrapper.querySelectorAll("img").forEach(
      (image) => (image.style.display = "none"),
    );

    // Render the image selection
    this.Selectors.ColourSelectionImageWrapper.querySelector(
      `[data-colour-map=${colourMap}]`,
    ).style.display = "block";
  };
}
